export type UserGardenCountryCode = 'US' | 'CA';

export interface UserGardenLocation {
  countryCode: UserGardenCountryCode;
  subdivisionCode: string;
  zip?: string;
  postalCode?: string;
  source: 'zip' | 'manual' | 'profile';
}

const STORAGE_KEY = 'garden-user-location';

const US_STATE_NAMES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
};

const CANADA_SUBDIVISION_NAMES: Record<string, string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

interface ZippopotamPlace {
  'state abbreviation'?: string;
  state?: string;
}

interface ZippopotamResponse {
  places?: ZippopotamPlace[];
}

function normalizeSubdivisionCode(countryCode: UserGardenCountryCode, value: string): string | null {
  const normalized = value.trim().toUpperCase();
  const prefix = `${countryCode}-`;
  const suffix = normalized.startsWith(prefix) ? normalized.slice(prefix.length) : normalized;
  const names = countryCode === 'US' ? US_STATE_NAMES : CANADA_SUBDIVISION_NAMES;

  if (!names[suffix]) return null;
  return `${countryCode}-${suffix}`;
}

export function normalizeUserGardenLocation(value: unknown): UserGardenLocation | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const source = value as Partial<UserGardenLocation>;
  const countryCode = source.countryCode === 'US' || source.countryCode === 'CA'
    ? source.countryCode
    : null;
  if (!countryCode || typeof source.subdivisionCode !== 'string') return null;

  const subdivisionCode = normalizeSubdivisionCode(countryCode, source.subdivisionCode);
  if (!subdivisionCode) return null;

  return {
    countryCode,
    subdivisionCode,
    ...(typeof source.zip === 'string' && source.zip.trim() ? { zip: source.zip.trim() } : {}),
    ...(typeof source.postalCode === 'string' && source.postalCode.trim() ? { postalCode: source.postalCode.trim() } : {}),
    source: source.source === 'manual' || source.source === 'profile' ? source.source : 'zip',
  };
}

export function getSubdivisionLabel(subdivisionCode: string): string {
  const normalized = subdivisionCode.trim().toUpperCase();
  const [countryCode, subdivision] = normalized.split('-') as [UserGardenCountryCode | undefined, string | undefined];
  if (!countryCode || !subdivision) return normalized;

  if (countryCode === 'US') return US_STATE_NAMES[subdivision] ?? normalized;
  if (countryCode === 'CA') return CANADA_SUBDIVISION_NAMES[subdivision] ?? normalized;
  return normalized;
}

export function getStoredUserGardenLocation(): UserGardenLocation | null {
  if (typeof window === 'undefined') return null;
  try {
    const direct = localStorage.getItem(STORAGE_KEY);
    if (direct) {
      const directLocation = normalizeUserGardenLocation(JSON.parse(direct));
      if (directLocation) return directLocation;
    }

    const rawZone = localStorage.getItem('garden-user-zone');
    if (!rawZone) return null;
    const zoneData = JSON.parse(rawZone) as { gardenLocation?: unknown; location?: unknown };
    return normalizeUserGardenLocation(zoneData.gardenLocation ?? zoneData.location);
  } catch {
    return null;
  }
}

export function saveStoredUserGardenLocation(location: UserGardenLocation | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (!location) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
  } catch {
    // Ignore storage failures.
  }
}

export async function getUserGardenLocationFromZip(zip: string): Promise<UserGardenLocation | null> {
  if (!/^\d{5}$/.test(zip)) return null;

  try {
    const res = await fetch(`https://api.zippopotam.us/us/${zip}`, {
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return null;

    const data = (await res.json()) as ZippopotamResponse;
    const abbreviation = data.places?.[0]?.['state abbreviation'];
    if (!abbreviation) return null;

    return normalizeUserGardenLocation({
      countryCode: 'US',
      subdivisionCode: `US-${abbreviation}`,
      zip,
      source: 'zip',
    });
  } catch {
    return null;
  }
}
